/**
 * PopupBlock V2
 * Razor renders markup; this toggles show/hide, auto-close, and one-time storage.
 */
import { devLogger } from "@/utils/dev-logger.util";
const STORAGE_PREFIX = "popup-v2:";
const SHOW_ONCE = "once";
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const SCROLL_LOCK_ATTR = "data-popup-block-scroll-lock";
/** Must match panel/backdrop `duration-*` in PopupBlockV2/Index.cshtml. */
const CLOSE_FADE_MS = 500;
const CLOSING_ATTR = "data-closing";
const MIN_SHOW_DELAY_MS = 50;
function setPageScrollLocked(locked) {
    document.documentElement.toggleAttribute(SCROLL_LOCK_ATTR, locked);
}
function storageKey(popupId) {
    return `${STORAGE_PREFIX}${popupId}`;
}
function readStorage(key) {
    try {
        return localStorage.getItem(key);
    }
    catch {
        return null;
    }
}
function writeStorage(key, value) {
    try {
        localStorage.setItem(key, value);
    }
    catch {
        // Ignore private mode / quota errors.
    }
}
function clearStorage(key) {
    try {
        localStorage.removeItem(key);
    }
    catch {
        // Ignore.
    }
}
function wasDismissed(popupId) {
    const key = storageKey(popupId);
    const raw = readStorage(key);
    if (!raw)
        return false;
    if (raw === "1")
        return true;
    try {
        const { expiresAt } = JSON.parse(raw);
        if (!expiresAt)
            return true;
        if (Date.now() > expiresAt) {
            clearStorage(key);
            return false;
        }
        return true;
    }
    catch {
        return false;
    }
}
function markDismissed(popupId, showAgainAfterDays) {
    const key = storageKey(popupId);
    if (showAgainAfterDays <= 0) {
        writeStorage(key, "1");
        return;
    }
    const expiresAt = Date.now() + showAgainAfterDays * MS_PER_DAY;
    writeStorage(key, JSON.stringify({ expiresAt }));
}
function parsePositiveInt(value) {
    const parsed = Number.parseInt(value || "0", 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}
function clearTimer(timer) {
    if (timer !== null)
        clearTimeout(timer);
    return null;
}
export function PopupBlock(element) {
    const popupId = element.dataset.popupId || "0";
    const isOnce = (element.dataset.showOption || SHOW_ONCE) === SHOW_ONCE;
    const autoCloseMs = parsePositiveInt(element.dataset.autoCloseMs);
    const delayMs = parsePositiveInt(element.dataset.delayMs);
    const showAgainAfterDays = parsePositiveInt(element.dataset.showAgainAfterDays);
    const closeBtn = element.querySelector("[data-popup-close]");
    const autoCloseProgress = element.querySelector("[data-popup-auto-close-progress]");
    let open = false;
    let showTimer = null;
    let autoCloseTimer = null;
    let closingTimer = null;
    const rememberOnce = () => {
        if (isOnce)
            markDismissed(popupId, showAgainAfterDays);
    };
    /** Draw progress ring from empty → full over the auto-close duration (no forced reflow). */
    const startAutoCloseProgress = () => {
        if (!autoCloseProgress || autoCloseMs <= 0)
            return;
        autoCloseProgress.style.transition = "none";
        autoCloseProgress.style.strokeDashoffset = "100";
        // Wait a frame so the empty state paints, then animate — avoids getBoundingClientRect reflow.
        requestAnimationFrame(() => {
            if (!open)
                return;
            autoCloseProgress.style.transition = `stroke-dashoffset ${autoCloseMs}ms linear`;
            autoCloseProgress.style.strokeDashoffset = "0";
        });
    };
    const setOpen = (visible) => {
        element.setAttribute("aria-hidden", visible ? "false" : "true");
    };
    const endClosingPhase = () => {
        closingTimer = clearTimer(closingTimer);
        element.removeAttribute(CLOSING_ATTR);
        setPageScrollLocked(false);
    };
    const close = () => {
        if (!open)
            return;
        open = false;
        showTimer = clearTimer(showTimer);
        autoCloseTimer = clearTimer(autoCloseTimer);
        closingTimer = clearTimer(closingTimer);
        // Keep z-50 via data-closing while opacity/transform fade out, then drop to -z-100.
        if (autoCloseMs > 0) {
            closeBtn?.setAttribute("disabled", "");
            closeBtn?.setAttribute("aria-busy", "true");
        }
        element.setAttribute(CLOSING_ATTR, "");
        setOpen(false);
        closingTimer = setTimeout(endClosingPhase, CLOSE_FADE_MS);
    };
    const openPopup = () => {
        if (open)
            return;
        open = true;
        closingTimer = clearTimer(closingTimer);
        element.removeAttribute(CLOSING_ATTR);
        rememberOnce();
        setPageScrollLocked(true);
        // Two frames so closed styles paint before reveal; otherwise the CSS transition can skip.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!open) {
                    setPageScrollLocked(false);
                    return;
                }
                setOpen(true);
                if (autoCloseMs > 0) {
                    startAutoCloseProgress();
                    autoCloseTimer = setTimeout(close, autoCloseMs);
                }
            });
        });
    };
    closeBtn?.addEventListener("click", close);
    if (!isOnce || !wasDismissed(popupId)) {
        showTimer = setTimeout(openPopup, Math.max(delayMs, MIN_SHOW_DELAY_MS));
    }
    else {
        devLogger.info(`PopupBlock ${popupId}: skipped (already dismissed)`);
    }
    return () => {
        open = false;
        showTimer = clearTimer(showTimer);
        autoCloseTimer = clearTimer(autoCloseTimer);
        closingTimer = clearTimer(closingTimer);
        closeBtn?.removeEventListener("click", close);
        element.removeAttribute(CLOSING_ATTR);
        setOpen(false);
        setPageScrollLocked(false);
    };
}
